/* LawyerProfile — بِطاقة محامٍ احترافيّة بِملء الشّاشة (مُستوحاة من بِطاقة CEO Helcoo).
   الخَلفيّة الخَضراء الداكِنة + اسم بارِز + QR vCard شَغّال + مُشارَكة كصورة PNG. */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  WhatsappLogo, X, ChatCircleDots, ShareNetwork, DownloadSimple,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { publicLawyerProfile, avatarUrl } from "../lib/api";
import { toWhatsappPhone, LEXIQ_BRAND, LEXIQ_TAGLINE } from "../lib/clientReminder";
import { honorific, detectGender } from "../lib/arabicGender";
import { copyShareUrl, nativeShare, buildWhatsappShareLink } from "../lib/lawyerShare";
import { gradeLabel } from "../lib/lawyerVerification";
import { startDm } from "../lib/messagingApi";
import { getUserToken } from "../lib/userApi";

/* ============================ بَيانات vCard ============================ */
function buildVCard(u, phone, shareUrl) {
  // RFC-6350 vCard 3.0 — يَفتَح في جَهات الاتِّصال على iOS/Android
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${u.name || ""}`,
    `N:${u.name || ""};;;;`,
    u.specialty ? `TITLE:محامي · ${u.specialty}` : "TITLE:محامي",
    "ORG:LEXIQ — ليكس آي كيو",
    phone ? `TEL;TYPE=CELL,VOICE:+${phone}` : "",
    u.governorate || u.city ? `ADR;TYPE=WORK:;;${u.city || ""};${u.governorate || ""};;;العراق` : "",
    shareUrl ? `URL:${shareUrl}` : "",
    u.bio ? `NOTE:${u.bio.replace(/\n/g, " ")}` : "",
    "END:VCARD",
  ].filter(Boolean);
  return lines.join("\n");
}

function buildContactText(lawyer) {
  const h = honorific(lawyer.name);
  return [
    `السلام عليكم ${h.title} ${lawyer.name} ${h.respect}،`,
    "",
    `وَجدتُ ملفّك على منصّة LEXIQ وأَودّ استشارتك بخصوص قضيّة لديّ.`,
    "",
    "تَفاصيل القضيّة:",
    "[اكتب هنا]",
    "",
    "━━━━━━━━━━━━━━",
    `${LEXIQ_BRAND} — ${LEXIQ_TAGLINE}`,
  ].join("\n");
}

function lawyerSalutation(name) {
  return detectGender(name) === "female" ? "مَحامية" : "مَحامي";
}

/* ============================ زرّ إغلاق عائِم ============================ */
function CloseButton({ onClose }) {
  return createPortal(
    <button
      type="button"
      onClick={onClose}
      aria-label="إغلاق"
      data-testid="profile-back"
      className="fixed z-[100] w-11 h-11 rounded-full bg-white/95 backdrop-blur-md shadow-[0_6px_20px_rgba(0,0,0,0.20)] flex items-center justify-center active:scale-95 transition-transform"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 14px)",
        insetInlineEnd: "16px",
      }}
    >
      <X size={20} weight="bold" className="text-[#0A192F]" />
    </button>,
    document.body,
  );
}

/* ============================ خَطّ مَعلومة ============================ */
function InfoField({ label, value, testid, full = false, ltr = false }) {
  if (!value) return null;
  return (
    <div className={full ? "col-span-2" : ""} data-testid={testid}>
      <div className="text-[11px] font-bold text-white text-start">
        {label}
      </div>
      <div
        className="text-[13px] text-white font-bold mt-1 leading-snug break-words text-start"
        style={ltr ? { fontVariantNumeric: "tabular-nums" } : undefined}
      >
        {ltr ? <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{value}</span> : value}
      </div>
    </div>
  );
}

/* ================================ الصَّفحة ================================ */
export default function LawyerProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [avatarDataUrl, setAvatarDataUrl] = useState("");
  const cardRef = useRef(null);

  useEffect(() => {
    let alive = true;
    publicLawyerProfile(slug)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setErr("لم نَجد هذا المحامي"); });
    return () => { alive = false; };
  }, [slug]);

  // نُحَمِّل الـ avatar كَـ Data URL لِتَجَنُّب CORS taint عند تَصدير الصورة عَبر html-to-image
  useEffect(() => {
    const aid = data?.user?.avatar_id;
    if (!aid) { setAvatarDataUrl(""); return; }
    let alive = true;
    fetch(avatarUrl(aid))
      .then((r) => r.blob())
      .then((blob) => new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(blob);
      }))
      .then((url) => { if (alive) setAvatarDataUrl(url); })
      .catch(() => { if (alive) setAvatarDataUrl(""); });
    return () => { alive = false; };
  }, [data?.user?.avatar_id]);

  if (err) {
    return (
      <div dir="rtl" className="min-h-[100dvh] flex items-center justify-center font-cairo px-6 bg-[#FAFAFA]">
        <div className="text-center">
          <div className="text-[16px] font-bold text-[#0A192F] mb-2">{err}</div>
          <Link to="/lawyers" className="text-[#D4AF37] text-[13px] underline">الرُّجوع إلى الدليل</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div dir="rtl" className="min-h-[100dvh] flex items-center justify-center font-cairo text-[#5A6470]">جارٍ التَّحميل…</div>;
  }

  const u = data.user;
  const initials = (u.name || "؟").split(" ").slice(0, 2).map((s) => s[0] || "").join("");
  const phone = toWhatsappPhone(data.phone);
  const phoneDisplay = phone ? `+${phone.replace(/^(\d{3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4")}` : "";
  // اتِّجاه LTR وَترتيب صَحيح للرَّقم
  // مِثال: phone="9647838352222" → "+964 783 835 2222"
  const waLink = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(buildContactText(u))}` : null;
  const role = lawyerSalutation(u.name);
  const grade = gradeLabel(u.grade);
  const shareUrl = `${window.location.origin}/lawyers/${slug}`;
  const vcard = buildVCard(u, phone, shareUrl);

  const openInAppChat = async () => {
    if (!getUserToken()) {
      toast.info("سَجِّل دُخولك لِبَدء محادثة آمِنة داخل التطبيق");
      navigate(`/login?next=${encodeURIComponent(`/lawyers/${slug}`)}`);
      return;
    }
    try {
      setChatLoading(true);
      const conv = await startDm({ target_user_id: u.id, target_slug: slug });
      navigate(`/messages/${conv.id || conv._id || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تَعَذَّر فَتح المحادثة");
    } finally {
      setChatLoading(false);
    }
  };

  /** يَستَخدِم صورة OG المُوَلَّدة على الخادم (Python+Pillow) — مَوثوقة 100% على iOS/Android. */
  const shareCardAsImage = async () => {
    try {
      toast.loading("جارٍ تَجهيز البِطاقة…", { id: "share-card" });
      const ogUrl = `${window.location.origin}/api/lawyers/${encodeURIComponent(slug)}/og.png?t=${Date.now()}`;
      const res = await fetch(ogUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`og.png ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], `${u.name}-LEXIQ.png`, { type: "image/png" });

      toast.dismiss("share-card");

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${u.name} — مَحامي على LEXIQ`,
            text: `بِطاقة المحامي ${u.name} على مَنصّة LEXIQ\n${shareUrl}`,
          });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }

      // Fallback: تَنزيل الصُّورة
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${u.name}-LEXIQ.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("تَمّ تَنزيل البِطاقة كَصورة");
    } catch (e) {
      toast.dismiss("share-card");
      console.error("share err:", e);
      try { await copyShareUrl(slug); toast.success("تَمّ نَسخ رابِط البِطاقة"); }
      catch { toast.error("تَعَذَّر التَّصدير"); }
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-[100dvh] font-cairo flex flex-col items-stretch"
      style={{ background: "#F0F1F2" }}
      data-testid="lawyer-profile-page"
    >
      <CloseButton onClose={() => navigate("/lawyers")} />

      {/* ===================== البِطاقة الخَضراء (ملء الشاشة) ===================== */}
      <div
        className="flex items-center justify-center px-3"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 70px)",
          paddingBottom: "0px",
        }}
      >
        <article
          ref={cardRef}
          dir="rtl"
          data-testid="lawyer-card-main"
          className="w-full max-w-[460px] relative overflow-hidden"
          style={{
            backgroundColor: "#344D14",
            borderRadius: "28px",
            boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.04)",
            padding: "26px 24px 24px",
            color: "#FFFFFF",
          }}
        >
          {/* ============== الصَّفّ العُلوي: أَيقونة LEXIQ + كَلمة LEXIQ في الجِهة المُقابِلة ============== */}
          <header className="flex items-center justify-between mb-7">
            <img
              src="/lexiq-icon-512.png"
              alt="LEXIQ"
              className="w-14 h-14 object-contain rounded-[14px]"
              style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.25))" }}
            />
            <span
              className="text-[22px] font-extrabold text-white"
              style={{
                letterSpacing: "0.18em",
                textShadow: "0 1px 2px rgba(0,0,0,0.20)",
              }}
            >
              LEXIQ
            </span>
          </header>

          {/* ============== الصَّفّ الرَّئيس: سَعادة المحامي + اسم + صورة ============== */}
          <section className="flex items-start justify-between gap-3 mb-6">
            <div className="flex-1 min-w-0 pt-1">
              <div className="text-[11.5px] font-bold text-white mb-1">
                سَعادة {role === "مَحامية" ? "المحامية" : "المحامي"}
              </div>
              <h1
                className="text-[26px] sm:text-[28px] font-extrabold text-white leading-[1.18]"
                data-testid="profile-name"
                style={{ wordBreak: "break-word" }}
              >
                {u.name}
              </h1>
              {u.specialty && (
                <div className="text-[13px] text-white mt-2 font-bold" data-testid="profile-specialty">
                  {u.specialty}
                </div>
              )}
            </div>

            {/* الصُّورة — يَسار (end في RTL) */}
            <div
              className="w-[100px] h-[100px] rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0"
              style={{ boxShadow: "0 8px 20px rgba(0,0,0,0.30)", border: "3px solid rgba(255,255,255,0.95)" }}
            >
              {avatarDataUrl ? (
                <img
                  src={avatarDataUrl}
                  alt={u.name}
                  className="w-full h-full object-cover"
                  data-testid="profile-avatar"
                />
              ) : u.avatar_id ? (
                <img
                  src={avatarUrl(u.avatar_id)}
                  alt={u.name}
                  className="w-full h-full object-cover"
                  data-testid="profile-avatar"
                  crossOrigin="anonymous"
                />
              ) : (
                <span className="text-[28px] font-extrabold text-white tracking-tight">{initials}</span>
              )}
            </div>
          </section>

          {/* ============== الحُقول: الهاتف · العُنوان · الصَّلاحيّة — كُلّها في سَطر واحِد ============== */}
          <section className="grid grid-cols-3 gap-3 mb-6" data-testid="profile-fields">
            <InfoField label="الهاتف" value={phoneDisplay} testid="info-phone" ltr />
            <InfoField
              label="العُنوان"
              value={[u.city, u.governorate].filter(Boolean).join("، ") || u.governorate || u.city}
              testid="info-address"
            />
            {grade && (
              <InfoField label="الصَّلاحيّة" value={grade.short} testid="info-grade" />
            )}
          </section>

          {/* ============== رمز QR (vCard) ============== */}
          <section className="flex flex-col items-center" data-testid="profile-qr-section">
            <div
              className="bg-white p-3 rounded-[14px]"
              style={{ boxShadow: "0 6px 16px rgba(0,0,0,0.25)" }}
              data-testid="profile-qr"
            >
              <QRCodeSVG
                value={vcard}
                size={140}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#1A1A1A"
                marginSize={0}
              />
            </div>
          </section>

          {/* تَوقيع ناعِم في الأَسفل */}
          <div className="mt-4 pt-3 border-t border-white/20 text-center text-[10.5px] text-white tracking-[0.1em]">
            LEXIQ — أَوّل مَنصّة قانونيّة عِراقيّة مُتَكامِلة
          </div>
        </article>
      </div>

      {/* ===================== أَزرار التَّواصُل والمُشارَكة (خارِج البِطاقة) ===================== */}
      <div className="px-4 pt-3 pb-4 max-w-[460px] mx-auto w-full space-y-2">
        {/* صَفّ واحِد: WhatsApp + مُحادثة داخل التَّطبيق */}
        <div className={`grid ${waLink ? "grid-cols-2" : "grid-cols-1"} gap-2.5`}>
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="profile-whatsapp"
              className="flex items-center justify-center gap-1.5 px-3 py-3 rounded-full bg-[#25D366] text-white text-[13px] font-bold active:scale-[0.98] transition shadow-[0_6px_18px_rgba(37,211,102,0.35)]"
            >
              <WhatsappLogo size={18} weight="fill" />
              <span>WhatsApp</span>
            </a>
          )}

          <button
            type="button"
            onClick={openInAppChat}
            disabled={chatLoading}
            data-testid="profile-inapp-chat"
            className="flex items-center justify-center gap-1.5 px-3 py-3 rounded-full bg-[#0EA5E9] text-white text-[13px] font-bold active:scale-[0.98] transition shadow-[0_6px_18px_rgba(14,165,233,0.40)] disabled:opacity-60"
          >
            <ChatCircleDots size={18} weight="fill" />
            <span>{chatLoading ? "جارٍ الفَتح…" : "مُحادثة داخِل التَّطبيق"}</span>
          </button>
        </div>

        {/* زرّ ثاني: مُشارَكة البِطاقة كَصورة */}
        <button
          type="button"
          onClick={shareCardAsImage}
          data-testid="profile-share-btn"
          className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-white border border-[#EFEFEF] text-[#0A192F] text-[14px] font-bold active:scale-[0.98] transition"
        >
          <ShareNetwork size={18} weight="bold" />
          مُشارَكة البِطاقة كَصورة
        </button>
      </div>
    </div>
  );
}
